import { useRef, useState } from "react";
import styled from "styled-components";
import { cssVariables } from "../../styles";
import { Tooltip } from "../tooltip/Tooltip";

interface Props<T> {
  option: T;
  preselected: T | null;
  getListHtmlOptionLabel: (value: T | null) => string;
  onClick: (value: T) => void;
}

export const Option = <T extends { id?: string | number }>({
  option,
  preselected,
  getListHtmlOptionLabel,
  onClick,
}: Props<T>) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [mouseX, setMouseX] = useState<number | null>(null);
  const [mouseY, setMouseY] = useState<number | null>(null);
  const inputRef = useRef<HTMLDivElement>(null);
  const calculateShowTooltip = () => {
    if (inputRef.current?.offsetWidth && inputRef.current?.scrollWidth)
      return inputRef.current?.offsetWidth < inputRef.current?.scrollWidth;
    return false;
  };

  return (
    <OptionContainer
      id={option.id?.toString()}
      key={option.id}
      onMouseDown={(event: React.MouseEvent) => event.preventDefault()} // added to prevent onBlur event happening before onClick
      onClick={() => onClick(option)}
      showTooltip={showTooltip}
      onMouseOver={() => {
        setShowTooltip(calculateShowTooltip());
      }}
      onMouseMove={event => {
        // set +10 to make tooltip be a bit away from the cursor and prevent cursor going on top of tooltip
        setMouseX(event.clientX + 10);
        setMouseY(event.clientY + 10);
      }}
      style={{
        backgroundColor:
          preselected?.id === option.id
            ? cssVariables.colors.lightGray
            : cssVariables.colors.white,
      }}>
      <Text
        ref={inputRef}
        dangerouslySetInnerHTML={{
          __html: getListHtmlOptionLabel(option),
        }}
      />
      <Tooltip
        key={`${option.id}-tooltip`}
        left={mouseX || 0}
        top={mouseY || 0}>
        {getListHtmlOptionLabel(option)}
      </Tooltip>
    </OptionContainer>
  );
};

const OptionContainer = styled.li<{ showTooltip: boolean }>`
  list-style-type: none;
  height: 1rem;
  padding: 0 1rem;
  padding-top: 1rem;
  padding-bottom: 1rem;
  white-space: nowrap;
  overflow: hidden;
  cursor: pointer;
  &:hover {
    background-color: #f0f0f0 !important;
  }
  &:hover span {
    display: ${props => (props.showTooltip ? "block" : "none")};
  }
`;

const Text = styled.div`
  text-overflow: ellipsis;
  overflow: hidden;
`;
